/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit-html/directives/style-map.js';
import { URL_REGEX } from './form-field-specs.js';
const DEFAULT_PLACEHOLDER = 'https://...\nhttps://...';
const DEFAULT_SPLIT_PATTERN = String.raw `\s+`;
let ChromedashAttachments = class ChromedashAttachments extends LitElement {
    constructor() {
        super(...arguments);
        this.textareaRef = createRef();
        this.fileInputRef = createRef();
        this.chromedash_single_pattern = URL_REGEX;
        this.chromedash_split_pattern = DEFAULT_SPLIT_PATTERN;
    }
    // Note: Form element components cannot have their own styles.
    // Must render to light DOM, so sl-form-fields work as intended.
    createRenderRoot() {
        return this;
    }
    validate() {
        if (this.textareaRef?.value?.validate) {
            this.textareaRef.value.validate();
        }
    }
    handleFieldUpdated(e) {
        let fieldValue = e.target.value;
        this.value = fieldValue;
        this.dispatchEvent(new CustomEvent('sl-change'));
    }
    handleFileSelected(e) {
        let inputEl = e.target;
        if (inputEl.files?.[0]) {
            const file = inputEl.files?.[0];
            window.csClient
                .addAttachment(this.featureId, inputEl.name, file)
                .then(resp => {
                this.value += '\n' + resp.attachment_url;
                this.dispatchEvent(new CustomEvent('sl-change'));
            });
        }
    }
    renderOneThumbnail(url) {
        if (url == '') {
            return nothing;
        }
        const linkStyles = {
            display: 'inline-block',
            padding: 'var(--content-padding-quarter)',
            margin: 'var(--content-padding-half)',
            border: `2px solid var(--link-color)`,
            cursor: `zoom-in`,
        };
        const imgStyles = {
            maxHeight: '150px',
            maxWidth: '200px',
        };
        var imgSrc = url;
        if (url.includes('/feature/' + this.featureId + '/attachment/')) {
            imgSrc += '/thumbnail'; // If it an image we serve, try thumbnail.
        }
        return html `
      <a href=${url} target="_blank" style=${styleMap(linkStyles)}>
        <img style=${styleMap(imgStyles)} src=${imgSrc}></img>
      </a>
    `;
    }
    renderThumbnails() {
        if (!this.value?.length) {
            return nothing;
        }
        const items = this.value.split(new RegExp(this.chromedash_split_pattern));
        return items.map(url => this.renderOneThumbnail(url));
    }
    renderUploadButton() {
        const buttonContainerStyles = {
            display: 'inline-block',
            margin: 'var(--content-padding-half)',
        };
        return html `
      <input
        ${ref(this.fileInputRef)}
        id="file-field"
        type="file"
        name="screenshots"
        @change=${e => this.handleFileSelected(e)}
        accept="image/png, image/jpeg, image/webp, text/plain"
        style="display:none"
      />
      <div style=${styleMap(buttonContainerStyles)}>
        <sl-button
          id="upload-button"
          @click=${e => this.fileInputRef?.value?.click()}
        >
          <sl-icon slot="prefix" name="upload"></sl-icon>
          Upload image
        </sl-button>
        <div>Max size: 1MB</div>
      </div>
    `;
    }
    render() {
        return html `
      <chromedash-textarea
        ${ref(this.textareaRef)}
        size="small"
        name=${this.name}
        value=${this.value}
        multiple
        placeholder=${DEFAULT_PLACEHOLDER}
        pattern=${this.pattern}
        chromedash_single_pattern=${this.chromedash_single_pattern}
        chromedash_split_pattern=${this.chromedash_split_pattern}
        @sl-change=${this.handleFieldUpdated}
        @keyup=${this.handleFieldUpdated}
      ></chromedash-textarea>
      <div>${this.renderThumbnails()} ${this.renderUploadButton()}</div>
    `;
    }
    firstUpdated() {
        this.validate();
    }
    updated() {
        this.validate();
    }
};
__decorate([
    property({ type: String })
], ChromedashAttachments.prototype, "name", void 0);
__decorate([
    property({ type: String })
], ChromedashAttachments.prototype, "value", void 0);
__decorate([
    property({ type: String })
], ChromedashAttachments.prototype, "pattern", void 0);
__decorate([
    property({ type: String })
], ChromedashAttachments.prototype, "chromedash_single_pattern", void 0);
__decorate([
    property({ type: String })
], ChromedashAttachments.prototype, "chromedash_split_pattern", void 0);
__decorate([
    property({ type: Number })
], ChromedashAttachments.prototype, "featureId", void 0);
ChromedashAttachments = __decorate([
    customElement('chromedash-attachments')
], ChromedashAttachments);
export { ChromedashAttachments };
